import React from 'react';
import { Document, Page, View, Text, StyleSheet, Font } from '@react-pdf/renderer';

// --- 1. Styling Definitions ---

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 15,
    },
    title: {
        fontSize: 16,
        marginBottom: 10,
        textAlign: 'center',
        fontWeight: 'bold',
        color: '#004d40',
    },
    table: {
        display: 'flex',
        // Enforce full width of the page container
        width: '100%', 
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        // Essential for horizontal layout
        flexDirection: 'row', 
        alignItems: 'flex-start', // Ensures cells start at the top
        borderBottomWidth: 1,
        borderStyle: 'solid',
        borderColor: '#bfbfbf',
    },
    tableHeader: {
        backgroundColor: '#e0f7fa',
        flexDirection: 'row',
        alignItems: 'flex-start',
        borderBottomWidth: 2,
        borderStyle: 'solid',
        borderColor: '#004d40',
    },
    tableCell: {
        // Minimized font size for many columns on A4 Landscape
        fontSize: 7, 
        padding: 4,
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderColor: '#bfbfbf',
        // Minimal flex ratio
        flex: 1.0, 
        textAlign: 'left', // Default text alignment
    },
    headerCell: {
        backgroundColor: '#b2dfdb',
        fontWeight: 'bold',
        fontSize: 8, 
        padding: 6,
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderColor: '#004d40',
        // Minimal flex ratio
        flex: 1.0, 
        textAlign: 'left', 
    },
    // Styles for the descriptive column (SALESPERSON)
    descriptiveCell: {
        // Slightly wider than others to accommodate longer names
        flex: 1.2, 
        textAlign: 'left',
        // Note: No position properties (e.g., sticky) are used here as they break flex flow.
    },
    // Styles for numeric columns
    numericCell: {
        textAlign: 'right',
        flex: 1.0, // Minimal flex ratio
    },
    footerRow: {
        flexDirection: 'row',
        backgroundColor: '#80cbc4',
        borderTopWidth: 3,
        borderStyle: 'double',
        borderColor: '#000',
        fontWeight: 'bold',
    },
    footerCell: {
        fontSize: 10,
        fontWeight: 'bold',
        padding: 8,
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderColor: '#000',
        textAlign: 'right',
    },
});

// --- 2. Helper Functions ---

const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    if (header.toUpperCase().includes('ACCOUNT') || header.toUpperCase().includes('CUSTOMER') || header.toUpperCase().includes('SALESPERSON')) {
        return false;
    }
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

const formatHeader = (key) => key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

// Define the specific columns the user wants to total in the footer (from original component)
const FOOTER_TOTAL_KEYS = [
    'CASH SALES',
    'NETCASH SALES',
    'NETCASH SALESRETURN',
    'CREDIT SALES',
    'NETCREDIT SALES',
    'NETSALES RETURN',
    'VAT AMT',
    'COMMISSION',
    'NET SALES',
    'NET SALES EXCLVAT'
];

// --- 3. Main PDF Component ---

// Default internalGrandTotals to an empty object {} to prevent crash if not provided.
const SalesTableSalespersonAggregatedPDF = ({ rows, internalGrandTotals = {} }) => {
    const descriptiveColumnKey = 'SALESPERSON';

    if (rows.length === 0) {
        return (
            <Document>
                <Page size="A4" orientation="landscape" style={styles.page}>
                    <Text style={styles.title}>Salesperson Aggregated Report</Text>
                    <Text style={{ textAlign: 'center', fontSize: 12, marginTop: 20 }}>No sales data found.</Text>
                </Page>
            </Document>
        );
    }

    // Determine all display keys based on the first row
    const allKeys = Object.keys(rows[0]);
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );

    // Keys for the columns that will display a total in the footer, preserving display order
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
    const descriptiveHeader = formatHeader(descriptiveColumnKey);

    // A map to assign dynamic styles/flex to each column key
    const getColumnStyle = (key, isHeader = false) => {
        let style = isHeader ? styles.headerCell : styles.tableCell;
        let dynamicStyle = {};

        if (key === descriptiveColumnKey) {
            dynamicStyle = styles.descriptiveCell;
        } else if (isNumericHeader(key)) {
            dynamicStyle = styles.numericCell;
        }

        return [style, dynamicStyle];
    };

    // --- Footer Alignment Logic ---
    const totalDisplayColumns = 1 + displayKeys.length;
    let descriptiveSpan = totalDisplayColumns;

    if (totalValueKeys.length > 0) {
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndex = displayKeys.indexOf(firstTotalKey);

        if (firstTotalIndex !== -1) {
            descriptiveSpan = 1 + firstTotalIndex;
        }
    }
    // --------------------


    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <Text style={styles.title}>Salesperson Aggregated Report</Text>

                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableHeader}>
                        {/* 1. First Descriptive Column Header */}
                        <Text style={[styles.headerCell, styles.descriptiveCell]}>
                            {descriptiveHeader}
                        </Text>

                        {/* 2. Dynamic Headers */}
                        {displayKeys.map(key => (
                            <Text key={key} style={getColumnStyle(key, true)}>
                                {formatHeader(key)}
                            </Text>
                        ))}
                    </View>

                    {/* Table Body (Data Rows) */}
                    {rows.map((row, index) => (
                        <View key={`agg-acc-row-${index}`} style={styles.tableRow} wrap={false}>
                            {/* 1. First Descriptive Column Cell */}
                            <Text style={[styles.tableCell, styles.descriptiveCell]}>
                                {row[descriptiveColumnKey]}
                            </Text>

                            {/* 2. Data Cells */}
                            {displayKeys.map(key => (
                                <Text key={key} style={getColumnStyle(key)}>
                                    {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                                </Text>
                            ))}
                        </View>
                    ))}

                    {/* Fixed Grand Totals Footer */}
                    {totalValueKeys.length > 0 && (
                        <View style={styles.footerRow} wrap={false}>
                            {/* 1. Descriptive Span for the GRAND TOTAL label */}
                            {Array.from({ length: descriptiveSpan }).map((_, i) => {
                                const isLabelCell = i === descriptiveSpan - 1; // Last cell of the span
                                return (
                                    <Text
                                        key={`total-label-cell-${i}`}
                                        style={[
                                            styles.footerCell,
                                            // Determine flex based on the column it's replacing
                                            { flex: getColumnStyle(i === 0 ? descriptiveColumnKey : displayKeys[i - 1])[1].flex }, 
                                            // Apply invisible background for "empty" cells in the span
                                            !isLabelCell && { backgroundColor: '#80cbc4', color: '#80cbc4', borderRightWidth: 0, padding: 0 }, 
                                            { textAlign: 'right', borderRightWidth: 0 },
                                        ]}
                                    >
                                        {isLabelCell ? 'GRAND TOTAL:' : ' '}
                                    </Text>
                                );
                            })}

                            {/* 2. Data cells starting from the column where the totals begin */}
                            {displayKeys.slice(descriptiveSpan - 1).map(key => {
                                const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                                
                                // *** FIXED LINE: Safely access total value using || 0 ***
                                const totalValue = internalGrandTotals[key] || 0; 
                                
                                return (
                                    <Text key={key} style={[styles.footerCell, getColumnStyle(key)[1]]}>
                                        {isTotalKey ? formatNumber(totalValue) : ''}
                                    </Text>
                                );
                            })}
                        </View>
                    )}
                </View>
                <Text style={{ position: 'absolute', bottom: 10, right: 15, fontSize: 8 }}>
                    Page <Text render={({ pageNumber, totalPages }) => (`${pageNumber} of ${totalPages}`)} />
                </Text>
            </Page>
        </Document>
    );
};

export default SalesTableSalespersonAggregatedPDF;